import AppState from 'AppState';
import Alert from 'components/shared/Alert';
import React, { useContext, useEffect, useState } from 'react';
import { Field, FormRenderProps, useField } from 'react-final-form';
import { useLocation } from 'react-router';
import { Button } from 'reactstrap';
import { getHandleableException, RemovalException } from 'types/Exceptions';
import { ModelStatus } from 'shared-client/types/model-status';
import AutoSuggestContext from '../auto-suggest/AutoSuggestContext';
import AutoSuggestSingle from '../auto-suggest/AutoSuggestSingle';
import { AutoSuggestItem } from '../auto-suggest/Types';
import ExclamationCircleSVG from '../svg/ExclamationCircleSVG';
import EditContext from './EditContext';
import LocalizeMode from './LocalizeMode';

export type CulturePickerProps = Pick<FormRenderProps, 'handleSubmit'> & {
	onChange?: (event: any) => void;
};

/**
 * Custom hook that returns the list of available cultures sorted based on whether an item is
 * being edited/created
 */
export const useSortedCultures = () => {
	const { isCreateMode, primaryCulture } = useContext(EditContext);

	return AppState.cultures.sort((a, b) => {
		if (
			a.id === primaryCulture ||
			(b.name > a.name && b.id !== primaryCulture) ||
			(isCreateMode && a.isFallback && b.id !== primaryCulture)
		) {
			return -1;
		}

		if (
			b.id === primaryCulture ||
			(b.name < a.name && a.id !== primaryCulture) ||
			(isCreateMode && b.isFallback && a.id !== primaryCulture)
		) {
			return 1;
		}

		return 0;
	});
};

interface CulturePickerOption extends AutoSuggestItem {
	code: string;
}

/**
 * Displays available cultures in a dropdown that lets users view the whole record in the context
 * of a different Culture. This also defines the primary culture when a record is being created.
 * @param param0
 */
function CulturePicker({ handleSubmit, onChange }: CulturePickerProps) {
	const editContext = useContext(EditContext);
	const { entityDefinition, setCulture, currentCulture, primaryCulture, isCreateMode, setRedirectCulture } =
		editContext;

	const [confirmCultureChange, setConfirmCultureChange] = useState<string | undefined>();
	const sortedCultures = useSortedCultures();
	const loc = useLocation<any>();

	useEffect(() => {
		if (loc.state && loc.state.redirectCulture) {
			setCulture(loc.state.redirectCulture);
		}

		//eslint-disable-next-line
	}, []);

	const showCulture = (cultureId: string) => {
		const culture = sortedCultures.find((c) => c.id === cultureId);
		return culture ? culture.name : 'Unknown Culture';
	};

	const options: CulturePickerOption[] = sortedCultures.map((culture) => ({
		value: culture.id,
		label: culture.name + ` (${culture.code})` + (primaryCulture === culture.id ? ' (primary)' : ''),
		code: culture.code,
	}));

	const saveAndContinue = async (submissionType: ModelStatus | null, redirectCulture?: string) => {
		await editContext.setNextSubmissionState({ submissionType, submissionTime: null, doContinue: true });
		try {
			setRedirectCulture(redirectCulture);
			await handleSubmit();
		} catch (error) {
			const serverException = getHandleableException(error) as RemovalException;

			console.debug('CulturePicker caught handleable error...');

			if (serverException && serverException.exceptionCode === 'illegal_removal_operation') {
				setConfirmCultureChange(undefined);
			} else {
				console.error('Unable to handle handleable error during publish:', error);
			}
			return;
		}
	};

	if (!currentCulture) {
		return null;
	}

	let currentCultureLabel;

	for (const option of options) {
		if (option.value === currentCulture) {
			currentCultureLabel = option.label;
		}
	}

	return AppState.config.multiSiteEnabled &&
		(entityDefinition.localizeMode !== LocalizeMode.AllCultures || isCreateMode) &&
		options.length > 1 ? (
		<>
			<AutoSuggestSingle
				name="AutoSuggestFilter"
				label="Culture"
				forceEmptyLabel
				clearable={false}
				testId="culture-picker"
				value={{
					label: currentCultureLabel,
					value: currentCulture,
				}}
				onChange={(e) => {
					if (onChange) {
						onChange(e);
					}

					if (!e) {
						return;
					}

					const newCultureId = e.value;

					if (isCreateMode) {
						setConfirmCultureChange(newCultureId);
					} else {
						setCulture(newCultureId);
						setRedirectCulture(newCultureId);
					}
				}}
				autoSuggestOptions={{
					staticData: options,
				}}
				renderMenu={() => (
					<Field name="scope">
						{({ input: { value } }) => <CulturePickerMenu value={value} options={options} />}
					</Field>
				)}
			/>

			{confirmCultureChange ? (
				<Alert
					renderTitle={() => <>Please confirm how you would like to proceed.</>}
					isOpen={true}
					size="large"
					onConfirm={() => setConfirmCultureChange(undefined)}
					confirmButtonText="Cancel"
					color="primary"
					renderContents={() => (
						<>
							<div style={{ marginTop: '25px' }}>
								<Button
									color="primary"
									onClick={() => {
										setCulture(confirmCultureChange);
										setConfirmCultureChange(undefined);
									}}
									data-testid="culture-modal-change"
								>
									Change Primary Language to {showCulture(confirmCultureChange)}.
								</Button>
							</div>
							<div>
								<Button
									color="primary"
									onClick={() => {
										setConfirmCultureChange(undefined);
										saveAndContinue(ModelStatus.Published, confirmCultureChange);
									}}
									data-testid="culture-modal-publish"
								>
									Publish {showCulture(currentCulture)} as the primary.
								</Button>
							</div>
							<div>
								<Button
									color="primary"
									onClick={() => {
										setConfirmCultureChange(undefined);
										saveAndContinue(ModelStatus.Inactive, confirmCultureChange);
									}}
									data-testid="culture-modal-inactive"
								>
									Save {showCulture(currentCulture)} as inactive primary.
								</Button>
							</div>
						</>
					)}
				/>
			) : null}
		</>
	) : null;
}

interface CulturePickerMenuProps {
	value: any;
	options: CulturePickerOption[];
}

/**
 * Custom renderer for the menu displayed in the Culture AutoSuggest component
 * @param props
 */
function CulturePickerMenu(props: CulturePickerMenuProps) {
	const { downshift } = useContext(AutoSuggestContext);
	const { currentCulture, primaryCulture } = useContext(EditContext);
	const bulkRecordsField = useField('bulkRecords');
	const translationsField = useField('translations');

	if (!downshift) {
		console.warn(
			'Downshift not found. Please check that this component is being used as a renderMenu prop for an AutoSuggest component.',
		);
		return null;
	}

	const { getItemProps, getMenuProps } = downshift;
	const { value, options } = props;

	let scopedCultures: CulturePickerOption[] = [];
	let unscopedCultures: CulturePickerOption[] = [];

	if (!value) {
		// global scope, so all cultures are scoped
		scopedCultures = [...options];
	} else if (value.type === 'Site') {
		// a site was selected, so only cultures for that site or any of its hosts are scoped.
		let scopedCultureId;

		const siteMatch = AppState.sites.find((site) => site.id === value.value);

		if (siteMatch) {
			scopedCultureId = siteMatch.cultureId;
		}

		const scopedCulture = options.find((option) => option.value === scopedCultureId);

		if (!scopedCulture) {
			console.warn('Unable to find culture.');
			return null;
		}

		const scopedHostCultures = options.filter((c) =>
			siteMatch?.hosts.some((s) => s.cultureId === c.value && scopedCultureId !== c.value),
		);

		scopedCultures = [scopedCulture, ...scopedHostCultures];
		unscopedCultures = options.filter((culture) => !scopedCultures.find((sc) => sc.value == culture.value));
	} else if (value.type === 'Site Collection') {
		// a site collection was selected, so only cultures for each site and their hosts that are in that collection are scoped

		// there's a discrepancy between the scope field and the value provided by AppState.
		// AppState has a sites collection, but the scope field doesn't.
		// this is done on purpose to avoid doubling up the data sent down to the client.
		const sites = value.sites ? value.sites : AppState.siteCollections.find((sc) => sc.id === value.value)!.sites;
		const siteCollectionSiteIds: number[] = sites.map((site) => site.siteId);
		const cultureIds: string[] = [];

		for (const site of AppState.sites) {
			for (const siteId of siteCollectionSiteIds) {
				if (site.id === siteId.toString()) {
					cultureIds.push(site.cultureId);

					for (const siteHost of site.hosts) {
						if (siteHost.cultureId) {
							cultureIds.push(siteHost.cultureId);
						}
					}
				}
			}
		}

		const uniqueCultureIds = [...new Set(cultureIds)];
		unscopedCultures = [...options];

		for (const option of options) {
			for (const uniqueCultureId of uniqueCultureIds) {
				if (option.value === uniqueCultureId) {
					scopedCultures.push(option);
					unscopedCultures.splice(unscopedCultures.indexOf(option), 1);
				}
			}
		}
	}

	// this will allow us to quickly check if we have available translations for any given culture
	const cultureToCompletionMap = options.reduce((acc, value) => {
		// the primary culture always gets translations as it's the main record
		if (primaryCulture === value.value) {
			acc[value.value] = true;
			return acc;
		}

		let foundTranslation = false;

		// If this is a bulk record, check all records for translations
		if (bulkRecordsField && bulkRecordsField.input.value) {
			for (const bulkRecord of bulkRecordsField.input.value) {
				if (!bulkRecord.translations) {
					continue;
				}

				const currentRecordTranslations = bulkRecord.translations[value.value];
				if (!currentRecordTranslations) {
					continue;
				}

				foundTranslation = Object.values(currentRecordTranslations).filter((value) => value).length > 0;
				if (foundTranslation) {
					break;
				}
			}
		} else {
			const translationsForCulture = translationsField && translationsField.input.value[value.value];
			if (translationsForCulture) {
				// we do a simple truthy check against the value, so that null/undefined/empty strings are ignored
				foundTranslation = Object.values(translationsForCulture).filter((value) => value).length > 0;
			}
		}

		acc[value.value] = foundTranslation;

		return acc;
	}, {});

	if (scopedCultures.length > 0 || unscopedCultures.length > 0) {
		return (
			<MenuWrapper
				menuProps={{
					...getMenuProps({
						open: true,
					}),
				}}
			>
				{scopedCultures.length > 0 ? (
					<>
						<li className="autosuggest-menu-categorized__item-group-title">SCOPED</li>
						{scopedCultures.map((scopedCulture, index) => (
							<li
								key={index}
								className="autosuggest-menu-categorized__item"
								{...getItemProps({
									item: {
										label: scopedCulture.label,
										value: scopedCulture.value.toString(),
									},
									index: index,
									key: `${scopedCulture.value}-${index}`,
								})}
								data-testid={`culture-picker-${scopedCulture.code}`}
							>
								<MenuItemStatus completed={cultureToCompletionMap[scopedCulture.value]} />
								<CultureLabel option={scopedCulture} currentCulture={currentCulture} />
							</li>
						))}
					</>
				) : null}

				{unscopedCultures.length > 0 ? (
					<>
						<li className="autosuggest-menu-categorized__item-group-title">NOT SCOPED</li>
						{unscopedCultures.map((unscopedCulture, index) => (
							<li
								key={index}
								className="autosuggest-menu-categorized__item"
								{...getItemProps({
									item: {
										label: unscopedCulture.label,
										value: unscopedCulture.value.toString(),
									},
									index: index + scopedCultures.length,
									key: `${unscopedCulture.value}-${index + scopedCultures.length}`,
								})}
								data-testid={`culture-picker-${unscopedCulture.code}`}
							>
								<MenuItemStatus completed={cultureToCompletionMap[unscopedCulture.value]} />
								<CultureLabel option={unscopedCulture} currentCulture={currentCulture} />
							</li>
						))}
					</>
				) : null}
			</MenuWrapper>
		);
	}

	// this should never happen
	return (
		<MenuWrapper>
			<li className="autosuggest-menu-categorized__item-group-title">Invalid scope type.</li>
		</MenuWrapper>
	);
}

interface CultureLabelProps {
	option: CulturePickerOption;
	currentCulture?: string;
}

/**
 * Wraps display for Culture Label within the custom Menu
 */
function CultureLabel({ option, currentCulture }: CultureLabelProps) {
	return <>{option.value === currentCulture ? <strong>{option.label}</strong> : option.label}</>;
}

interface MenuWrapperProps {
	children: React.ReactNode;
	menuProps?: any;
}

/**
 * Reusable Menu Wrapper
 * @param props
 */
function MenuWrapper(props: MenuWrapperProps) {
	const { children, menuProps } = props;

	return (
		<div className="autosuggest-menu-categorized">
			<ul
				className="dropdown-menu autosuggest-menu-categorized__list autosuggest-menu-categorized__list--wide"
				{...menuProps}
			>
				{children}
			</ul>
		</div>
	);
}

interface MenuItemStatusProps {
	completed?: boolean;
}

function MenuItemStatus(props: MenuItemStatusProps) {
	const { completed = false } = props;

	return (
		<div className="autosuggest-menu-categorized__item-status">
			{!completed ? (
				<ExclamationCircleSVG class="autosuggest-menu-categorized__item-status-uncompleted" />
			) : (
				<span className="autosuggest-menu-categorized__item-status-completed"></span>
			)}
		</div>
	);
}

export default CulturePicker;
